import React, { useEffect, useState, useContext } from 'react'
import { mainTheme } from 'themes'
import { makeStyles } from '@material-ui/core/styles'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import { getMuiTheme, useRemindAccountSecurity } from 'password-and-session-management'
import { ThemeProvider, AccountSecurityProvider, useAccountSecurityContext, UserProvider, UserInfo, UserContext } from 'providers'
import { Loader } from 'foundations-library/components'
import Provider from './PasswordManagerAppProvider'
import Content from './Content'
import { RenderOptions } from '../types'
import MFABanner from 'password-and-session-management/src/banners/MFA'
import PasswordExpiryBanner from 'password-and-session-management/src/banners/PasswordExpiry'
import { initializePhoneCom } from 'phonecom'

const jss = () => {
    return {
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            '& button': {
                border: 0
            }
        },
        passwordManagement: ({ theme: optionsTheme }) => {
            const themingStyle = optionsTheme === 'mui' ? getMuiTheme() : {}
            return {
                ...themingStyle,
                marginBottom: '10px'

            }
        },
        loader: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            width: '100%'
        }
    }
}

const useStyles = makeStyles(jss)

const mobileSources = ['android', 'ios']

const RenderContent = ({ passwordManagementOptions, styles, setIsLoading }: { passwordManagementOptions: RenderOptions, styles: ClassNameMap<'root' | 'passwordManagement' | 'loader'>, setIsLoading: React.Dispatch<React.SetStateAction<boolean>> }) => {
    const source = passwordManagementOptions.source || 'web'
    const isMobile = mobileSources.includes(source)
    const [isLoadingSecurityData, setIsLoadingSecurityData] = useState(Boolean(isMobile ? passwordManagementOptions.sessionToken : false))
    const user = useContext(UserContext)
    const { passwordSecurityState } = useAccountSecurityContext()
    const { remindMFA, remindPasswordExpiry } = useRemindAccountSecurity()

    useEffect(() => {
        if (user) {
            setIsLoadingSecurityData(passwordSecurityState.isLoading)
        }
    }, [user, passwordSecurityState.isLoading])

    if (isLoadingSecurityData) return showLoader(styles)

    return (
        <>
            {isMobile && (remindMFA
                ? <MFABanner date={passwordSecurityState.data.mfaEnableLastDate!} />
                : remindPasswordExpiry ? <PasswordExpiryBanner date={passwordSecurityState.data.passwordExpiryDate!} /> : null)}
            <div className={styles.passwordManagement}>
                <Content {...passwordManagementOptions} />
            </div>
        </>
    )
}

const fetchUserInfo = async (sessionToken: string): Promise<UserInfo> => {
    window.xprops = {
        accessToken: sessionToken
    }
    const userInfo = await initializePhoneCom({})
    return userInfo
}

const showLoader = (styles: ClassNameMap<'root' | 'passwordManagement' | 'loader'>) => {
    return (
        <div className={styles.loader}>
            <Loader size='large' data-testid='password-manager-loader' />
        </div>
    )
}

/**
 *
 */
const App = ({ options }: { options: RenderOptions }): React.ReactElement => {
    const styles = useStyles({ theme: options.theme || 'foundations' })
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
    const [isLoading, setIsLoading] = useState(Boolean(options.sessionToken))
    const source = options.source || 'web'
    const isMobile = mobileSources.includes(source)

    useEffect(() => {
        let isMounted = true

        const loadUserInfo = async () => {
            if (!options.sessionToken || !isMobile) {
                setUserInfo(null)
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)
                const response = await fetchUserInfo(options.sessionToken)
                if (isMounted) setUserInfo(response)
            } catch (error) {
                console.error('Failed to fetch user info:', error)
            } finally {
                if (isMounted) setIsLoading(false)
            }
        }

        loadUserInfo()

        return () => {
            isMounted = false
        }
    }, [options.sessionToken, isMobile])

    const content = (
        <Provider options={options}>
            <AccountSecurityProvider sessionToken={isMobile ? options.sessionToken : undefined}>
                <RenderContent passwordManagementOptions={options} styles={styles} setIsLoading={setIsLoading} />
            </AccountSecurityProvider>
        </Provider>
    )

    return <div className={styles.root}>
        <ThemeProvider theme={mainTheme}>
            {isLoading
                ? showLoader(styles)
                : userInfo
                    ? <UserProvider userInfo={userInfo}>{content}</UserProvider>
                    : content
            }
        </ThemeProvider>
    </div>
}

export default App
